import { User } from "./users"
import { Artist } from "./artists"
import { PaginatedData } from "./root"
import { SpotifyArtistRequest } from "./spotifyArtistRequest"
import { Affiliate } from "./affiliates"

export enum ReportTabs {
  UNDEFINED = 0,
  TRACK_VALUATION_BUBBLE = 1,
  LTM_AND_FORECAST = 2,
  INCOME_AND_FORECAST = 3,
  MONTHLY_INCOME_AND_LISTENERS = 4,
  TOTAL_INCOME_AND_LISTENERS = 5,
  PLATFORM_AND_TERRITORY = 6,
  DISTRIBUTOR_PERCENTAGE = 7,
  TRACKS_INCOME_AND_LISTENER_PERCENTAGE = 8,
  PLATFORM_MONTHLY_INCOME = 9,
  PLATFORM_MONTHLY_LISTENER = 10,
  TRACK_TIMELINE = 11,
  DEAL_OFFERS = 12,
  ACTUAL_VS_PREDICTED = 13,
  RIGHTS_VERIFICATION = 14,
  EXCHANGE_RATE_UNAVAILABLE = 15,
  OFFER_SUMMARY = 16
}

export class AdminSongSelectionReportWidth {
  checkbox = "3%"
  link = "4%"
  isrc = ""
  title = "18%"
  albumId = ""
  ownership = "3%"
  userApproved = "3%"
  confirmedOffer = "6%"
  splitAppliedConfirmedOffer = "6%"
  offerUsd = ""
  advanceUsd = "6%"
  advance2 = "6%"
  releaseDAte = "6%"
  streamCount = "6%"
  streamDate = "6%"
  lmLsmLtmStream = "6%"
  lmLsmLtmIncome = "6%"
  Ltm456Income = ""
  reportLtmIncome = "6%"
  originalReportLtmIncome = "6%"
  trendAnalysis = "6%"
}

export class AdminSongSelectionWidth {
  checkbox = "3%"
  link = "4%"
  isrc = ""
  title = "20%"
  albumId = ""
  ownership = "3%"
  userApproved = "3%"
  splitAppliedConfirmedOffer = "7%"
  confirmedOffer = "6%"
  offerUsd = ""
  advanceUsd = "6%"
  advance2 = "6%"
  releaseDAte = "11%"
  streamCount = "6%"
  streamDate = "8%"
  lmLsmLtmStream = "7%"
  lmLsmLtmIncome = "7%"
  Ltm456Income = ""
  reportLtmIncome = ""
  originalReportLtmIncome = ""
  trendAnalysis = "6%"
}

export class AdminSongSelectionIdWidth {
  checkbox = "3%"
  link = "4%"
  isrc = ""
  title = "22%"
  albumId = ""
  ownership = ""
  userApproved = ""
  confirmedOffer = "9%"
  splitAppliedConfirmedOffer = "10%"
  offerUsd = ""
  advanceUsd = ""
  advance2 = ""
  releaseDAte = "9%"
  streamCount = "8%"
  streamDate = "7%"
  lmLsmLtmStream = "8%"
  lmLsmLtmIncome = "8%"
  Ltm456Income = ""
  reportLtmIncome = "6%"
  originalReportLtmIncome = ""
  trendAnalysis = "6%"
}

export class UserSongSelectionWidth {
  checkbox = "4%"
  link = "6%"
  isrc = ""
  title = "36%"
  ownership = ""
  userApproved = ""
  albumId = ""
  confirmedOffer = ""
  splitAppliedConfirmedOffer = ""
  offerUsd = ""
  advanceUsd = ""
  advance2 = ""
  releaseDAte = "16%"
  streamCount = "12%"
  streamDate = ""
  lmLsmLtmStream = "10%"
  lmLsmLtmIncome = ""
  Ltm456Income = ""
  reportLtmIncome = "10%"
  originalReportLtmIncome = ""
  trendAnalysis = "6%"
}

export enum ConfirmedOfferTypes {
  ADVANCE = "advance",
  BUYOUT = "buyout"
}

export enum RoyaltyReportTypes {
  INDIVIDUAL = "individual",
  LABEL = "label"
}

export enum ReportUploadRequestStatus {
  UPLOAD_IN_PROGRESS = "upload_in_progress",
  UPLOAD_FAILED = "upload_failed",
  UPLOAD_PARTIAL_SUCCESS = "upload_partial_success",
  DRAFT = "draft", // after reports uploaded succesfully
  REVIEWING = "reviewing", // when "create confirmed offer request" button clicked
  REQUEST_APPROVED = "request_approved",
  REQUEST_DISAPPROVED = "request_disapproved"
}

export type RoyaltyReportRow = {
  distributor: string
  period: string
  platform: string
  transactionDate: string
  songTitle: string
  isrc: string
  created: string
  territory: string
  quantity: number
  currency: string
  segment: string
  segmentDescription: string
  upc: string
  grossProgress: number
  netProgress: number
  splitRate: number
  channelId: string
  isVideo: boolean
}

export type RoyaltyReport = {
  user: User
  userId: number
  sheetId: string
  spotifyId: string
  artist: Artist
  progress: number
  validIsrc: boolean
  created: string
  distributor: string
  type: string
  currency: string
  unitPrice: number
  grossUnitPrice?: number
  error: string
  warning: string
  rawFileId: string
  missingPlatforms?: string
  missingPlatformsRisk?: []
  missingPlatformsOpportunity?: []
  unsupportedPlatforms?: string
  reportUploadRequestId?: number
  reportUploadRequest?: ReportUploadRequest
  extendedSheetsFolderName?: string
  hasPeriodError?: boolean
}

export interface ConfirmedOfferOption {
  id: number
  name: string
  value: string
  description: string
  multiplier: number
  type: string
  primary: boolean
}

export interface SongOffer {
  title: string
  isrc: string
  offer: string
}

export interface SongMetadata {
  key: string
  value: string
}

export enum ContractModel {
  FIXED_TERM = "fixed_term",
  POST_RECOUP = "post_recoup"
}

export interface DealOffer {
  id?: number
  confirmedOffer?: ConfirmedOffer
  confirmedOfferId?: number
  offer?: number
  version?: number
  type?: string
  status?: string
  currency?: string
  songOffers?: string
  newReleaseInclude?: boolean
  newReleaseCommissionRate?: string
  newReleasePostCommissionRate?: string
  newReleaseIncludeRecoup?: boolean
  contractDuration?: string
  contractModel?: ContractModel
  postRecoupDuration?: number
  contractPreRecoupCommission?: string
  contractPostRecoupCommission?: string
  contractExclusivity?: boolean
  approved?: boolean
  description?: string
  changes?: string
  userId?: number
  user?: User
  created?: string
  songOfferList?: SongOffer[]
  songChangeList?: SongOffer[]
  metadataChangeList?: SongMetadata[]
  recoupBase?: string
  catalogDefinition?: string
  syncRights?: boolean
  targetRecoupMonths?: string
  payoutFrequency?: string
  payoutThreshold?: string
  payoutCurrency?: string
  assignedScout?: Affiliate | null
  assignedScoutId?: number | null
}

export type ExchangeRateIssueCurrency = {
  currency: string
  // Stays in its original currency: there is no exchange rate to convert it with.
  total: number
}

// Set for the deals whose valuation had to skip revenue for want of an exchange rate - the same
// problem the Offer Detail page shows on its "Exchange Rate Unavailable" tab. Precomputed by the
// exchange_rate_unavailable_sync cronjob, so the list endpoint just reads it.
export type ConfirmedOfferExchangeRateIssue = {
  // Biggest amount first.
  currencyTotals: ExchangeRateIssueCurrency[]
  currencyCount: number
}

export interface ConfirmedOffer {
  id: number
  spotifyId: string
  created: string
  advanceOffer: number
  buyoutOffer: number
  defaultBuyout: number
  defaultAdvance: number
  defaultAdvance2: number
  approved: boolean
  artist: Artist
  user: User
  userApproved: boolean
  selectedOption: ConfirmedOfferOption
  confirmedTotal: number
  type: string
  spotifyArtistAccount: SpotifyArtistRequest
  songCount?: number
  offerCount?: number
  spotifyCount?: number
  splitRate?: number
  reportLtmIncome?: number
  title?: string
  trendThree?: number
  trendSix?: number
  trendTwelve?: number
  dealOffers: DealOffer[]
  currency?: string
  reportCurrencyConversion: number
  flag?: string
  exchangeRateIssue?: ConfirmedOfferExchangeRateIssue | null
}

export interface AgreementUpload {
  id: number
  user: User
  userId: string
  artistId: string
  sheetId: string
  progress: number
  created: string
}

export interface ReportUploadRequest {
  id: number
  user: User
  userId: string
  spotifyId: string
  artist: Artist
  driveFolderId: string
  distributor: string
  status: string
  created: string
  type: string
  royaltyReportId?: string
  royaltyReport?: RoyaltyReport
  description?: string
}

export interface ReportUploadRequestFile {
  id: number
  name: string
  distributor: string
  fileId: string
  folderId: string
  previewRows: string[][]
  approved: boolean // if the file is approved and used to create the RoyaltyReport, then it is true
  groupName: string // It will be used to group the files based on distributorName-headers, hasHeaderMismatch will not be used
  premappedHeaders?: { [key: string]: string | boolean } // id this file has been approved before, then it's mappedHeaders should be already created, then we can use it.
  issueFlags?: DataIssueFlagMap
}

export enum DataIssueFlagTypes {
  CURRENCY_ISSUE = "currency_issue",
  DUPLICATE_ISRC = "duplicate_isrc",
  MISSING_DATA = "missing_data",
  MISSING_TERRITORIES = "missing_territories"
}

export type DataIssueFlagMap = Record<DataIssueFlagTypes, boolean>

export type CurrencyExchangeRate = {
  [currency: string]: number
}

export type ReportsContextType = {
  paginatedConfirmedOfferList: PaginatedData<ConfirmedOffer>
  fetchConfirmedOfferList: (
    limit: number,
    offset: number,
    isPublic: boolean,
    filter?: ConfirmedOffersFilterType,
    orderBy?: string,
    id?: number,
    userId?: number,
    isDealOffers?: boolean
  ) => Promise<void>
  fetchDealStatusCounts: (filter?: ConfirmedOffersFilterType, isDealOffers?: boolean) => Promise<DealOverviewCounts>
  updateConfirmedOffer: (
    id: number,
    approved: boolean,
    type: string,
    splitRate?: number,
    offer?: number
  ) => Promise<boolean>
  saveNewVersionConfirmedOffer: (id: number, type: string, offer: number) => Promise<boolean>
  mergeConfirmedOffers: (confirmedOfferIds: number[]) => Promise<boolean>
  updateSongSelectionOffer: (confirmedOfferId: number, songSelectionId: number, offer: number) => Promise<boolean>
  bulkUpdateSongSelectionOffer: (
    confirmedOfferId: number,
    updates: { songSelectionId: number; offer: number }[]
  ) => Promise<boolean>
  paginatedAgreementUploadList: PaginatedData<AgreementUpload>
  fetchAgreementUploadList: (
    limit: number,
    offset: number,
    filter?: AgreementUploadFilterType,
    orderBy?: string
  ) => Promise<void>
  uploadAgreementFile: (formData: any, artistId: number) => Promise<boolean>
  paginatedReportUploadRequestList: PaginatedData<ReportUploadRequest>
  fetchReportUploadRequestList: (
    limit: number,
    offset: number,
    filter?: ReportUploadRequestFilterType,
    orderBy?: string
  ) => Promise<void>
  currencyExchangeRates: CurrencyExchangeRate
  fetchCurrencyExchangeRates: () => Promise<void>
  dealOfferStatistics: DealOfferStatistics | null
  fetchDealOffersStatistics: (filter?: ConfirmedOffersFilterType) => Promise<void>
  getVariationTemplates: () => Promise<OfferVariationTemplate[]>
  calculateRecoup: (
    confirmedOfferId: number,
    variations: RecoupVariationInput[],
    predictionVersion?: number
  ) => Promise<RecoupResult[]>
  getArtistProfile: (confirmedOfferId: number) => Promise<ArtistProfileBundle>
  dealOfferBi: DealOfferBi | null
  fetchDealOffersBi: (filter?: ConfirmedOffersFilterType) => Promise<void>,
  deleteDealOffer: (id: number) => Promise<boolean>
}

export interface ReportsProps {
  paginatedConfirmedOfferList: PaginatedData<ConfirmedOffer>
  paginatedAgreementUploadList: PaginatedData<AgreementUpload>
  paginatedReportUploadRequestList: PaginatedData<ReportUploadRequest>
  currencyExchangeRates: CurrencyExchangeRate
  dealOfferStatistics: DealOfferStatistics | null
  dealOfferBi: DealOfferBi | null
}

export interface ReportsActionProps {
  type: string
  payload?: ReportsProps
}

export interface ConfirmedOfferTransferRequest {
  confirmedOfferId: number
  spotifyAccountId: number
  firstName: string
  lastName: string
  email: string
  password: string
  spotifyId: string
  allowedOfferLinkCode: string
  overrideAllowedOfferLinkCode: boolean
  duplicateOffer: boolean
  [key: string]: string | number | boolean
}

export type ConfirmedOfferSort = {
  id: number
  user_id: number
  spotify_id: number
  created: number
  advance_offer: number
  buyout_offer: number
  default_advance: number
  default_advance_2: number
  default_buyout: number
  approved: number
  user_approved: number
  selected_option_id: number
  confirmed_total: number
  type: number
  user__username: number
  spotify_artist_account_id: number
  spotify_artist_account__report_uploaded: number
  spotify_artist_account__timeline: number
  trend_twelve: number
  trend_six: number
  trend_three: number
  [key: string]: number
}

export const initialConfirmedOfferSort: ConfirmedOfferSort = {
  id: -1,
  user_id: 0,
  spotify_id: 0,
  created: 0,
  advance_offer: 0,
  buyout_offer: 0,
  default_advance: 0,
  default_advance_2: 0,
  default_buyout: 0,
  approved: 0,
  user_approved: 0,
  selected_option_id: 0,
  confirmed_total: 0,
  type: 0,
  user__username: 0,
  spotify_artist_account_id: 0,
  spotify_artist_account__report_uploaded: 0,
  spotify_artist_account__timeline: 0,
  trend_twelve: 0,
  trend_six: 0,
  trend_three: 0
}

export type ConfirmedOffersFilterType = {
  artistName: string
  artistSpotifyId: string
  username: string
  rawBuyoutOffer?: number[]
  rawAdvanceOffer?: number[]
  confirmedBuyoutOffer?: number[]
  confirmedAdvanceOffer?: number[]
  songCount?: number[]
  offerCount?: number[]
  country: string
  genre: string
  hotList?: boolean
  ignore?: boolean
  salesFunnel?: boolean
  contactStatus?: boolean
  accountType?: string
  contactType?: string
  service?: string
  tagsIncluded: string[]
  tagsExcluded: string[]
  emailExist?: boolean
  phoneExist?: boolean
  pipelineStatus?: boolean
  inbox?: boolean
  trendThree?: number
  trendSix?: number
  trendTwelve?: number
  id?: number
  dealStatus?: string
  // Only ever true or undefined: getFilterText would send "false" as a query param.
  exchangeRateUnavailable?: boolean
  dealDateRange?: [Date | null, Date | null]
  dealType?: string
  diyGroup?: boolean
  distributorId?: number
  listenerMin?: number
  listenerMax?: number
  showExcluded?: boolean
  countryDimension?: string
}

export type DealStatusCount = {
  count: number
  amount: number
}

export type DealStatusCounts = {
  [status: string]: DealStatusCount
}

export type DealOverviewCounts = {
  statusCounts: DealStatusCounts
  // Deals whose valuation had to skip revenue for want of an exchange rate.
  exchangeRateUnavailable: DealStatusCount
}

export type ConfirmedOfferSongSelectionSort = {
  track_title: number
  isrc: number
  track_albumId: number
  selected: number
  ownership: number
  advanceOffer: number
  buyoutOffer: number
  defaultAdvance: number
  defaultBuyout: number
  defaultAdvance2: number
  ltmIncome: number
  originalLtmIncome: number
  track_streamsTotal: number
  track_streamDate: number
  track_album_releaseDate: number
  track_ltm: number
  track_trendThree: number
  [key: string]: number
}

export const initialConfirmedOfferSongSelectionSortSort: ConfirmedOfferSongSelectionSort = {
  track_title: 0,
  isrc: 0,
  track_albumId: 0,
  selected: 0,
  ownership: 0,
  advanceOffer: 0,
  buyoutOffer: 0,
  defaultAdvance: 0,
  defaultBuyout: 0,
  defaultAdvance2: 0,
  ltmIncome: 0,
  originalLtmIncome: 0,
  track_streamsTotal: 0,
  track_streamDate: 0,
  track_ltm: 0,
  track_trendThree: 0,
  track_album_releaseDate: 0
}

export type ReportUploadColumn = {
  id?: number
  type?: string
  header?: string
}

export type AgreementUploadFilterType = {
  artistId?: number
}

export type ReportUploadRequestFilterType = {
  id?: number
  distributorList?: string[]
  statusList?: string[]
  artistSpotifyId?: string
}
export type MonthlyDealData = {
  month: string // Format: "Jan-25", "Feb-25", etc.
  underNegotiation: {
    ticketSize: number
    ltm: number
    songCount: number
    offerCount: number
  }
  agreed: {
    ticketSize: number
    ltm: number
    songCount: number
    offerCount: number
  }
  declined: {
    ticketSize: number
    ltm: number
    songCount: number
    offerCount: number
  }
  total: {
    ticketSize: number
    ltm: number
    songCount: number
    offerCount: number
  }
}

// The FRAUD bucket of the deals report. Reported next to - never inside - the figures
// above: fraud flagged deals are excluded from every aggregate by the backend (KAH-60598).
export type FraudSummary = {
  totalOffers: number
  totalOfferAmount: number
  artistCount: number
}

export type DealOfferStatistics = {
  totalOffers: number
  statusCounts: {
    [key: string]: number
  }
  typeCounts: {
    [key: string]: number
  }
  userCounts: {
    [key: string]: number
  }
  currencyCounts: {
    [key: string]: number
  }
  totalOfferAmount: number
  averageOfferAmount: number
  monthlyBreakdown?: MonthlyDealData[]
  fraudSummary?: FraudSummary
}

export type OfferVariationName = "conservative" | "balanced" | "aggressive" | "custom"

export interface OfferVariationTemplate {
  id: number
  name: Exclude<OfferVariationName, "custom">
  displayName: string
  description: string
  advanceMultiplier: number
  preRecoupSplitRate: number
  preRecoupCommission: number
  postRecoupCommission: number | null
  contractDurationYears: number
  postRecoupDurationYears: number | null
  exclusivity: boolean
  targetRecoupMonths: number | null
  newReleaseIncluded: boolean
  newReleasePreRecoupCommission: number
  newReleasePostRecoupCommission: number
  payoutFrequency: string
  payoutThreshold: number
  isActive: boolean
  ordering: number
  maxAdvancePctOfForecast: number
  maxRecoupOvershootMonths: number
  minPreRecoupCommission: number
  minContractDurationYearsForHighAdvance: number
  highAdvanceMultiplierThreshold: number
}

export interface RecoupVariationInput {
  name: OfferVariationName
  advance: number
  preRecoupCommission: number
  postRecoupCommission?: number | null
  contractDurationYears: number
  exclusivity?: boolean
  unitPriceOverride?: number | null
  isCustom?: boolean
}

export interface OfferWarning {
  code: string
  message: string
  severity: "warning"
}

export interface RecoupResult {
  name: OfferVariationName
  recoupMonths: number | null
  recoupDate: string | null
  totalPredictedRevenue: number
  totalRecoupPool: number
  contractHorizonMonths: number
  recoupedWithinContract: boolean
  ltmIncome: number
  warnings: OfferWarning[]
  assumptions: {
    unitPrice: number
    predictionVersion: number
    songCount: number
    serviceCost: number
  }
}

export interface ArtistProfileBundle {
  artist: {
    id: number
    artistId: string
    name: string
    image: string | null
    ltmStreams: number
    lsmStreams: number
    lmStreams: number
    spFollower: number
    spMonthlyListener: number
    spPopularity: number
    trendTwelve: number | null
    trendSix: number | null
    trendThree: number | null
    country: string | null
  } | null
  mostStreamedTrack: {
    id: number
    title: string
    isrc: string
    streamsTotal: number
    ltm: number
    image: string | null
  } | null
  recentRelease: {
    title: string
    releaseDate: string
    image: string | null
  } | null
  topCountries: Array<{
    country: string
    city?: string | null
    percentFollowers: string
    followers: number
    source: string
  }>
  tiktok: {
    profileUrl: string | null
    soundUsage: number | null
  }
}

export type DealOfferBi = {
  totalOffers: number
  statusCounts: {
    [key: string]: number
  }
  totalOfferAmount: number
  monthlyBreakdown?: MonthlyDealData[]
  advanceBuckets?: AdvanceBucketStat[]
  countryDistribution?: CountryDistributionStat[]
  listenerDistribution?: ListenerBucketStat[]
  competitorDisplacement?: CompetitorStat[]
  recoupmentDistribution?: RecoupmentDistribution
  fraudSummary?: FraudSummary
}

export type RecoupmentBucket = {
  bucket: string
  count: number
}

export type RecoupmentDistribution = {
  buckets: RecoupmentBucket[]
  medianMonths: number | null
  sampleSize: number
  // Aggregate `recoup_month_total` from royalty user_type=total — mirrors the
  // "Overall Avg. Recoup Time" metric shown in the artist admin Performance
  // page so the two products report the same headline number.
  aggregateAvgMonths?: number | null
}

export type AdvanceBucketStat = {
  bucket: string
  total: number
  accepted: number
  closed: number
}

export type CountryDistributionStat = {
  country: string
  total: number
  accepted: number
}

export type ListenerBucketStat = {
  bucket: string
  total: number
  accepted: number
}

export type CompetitorStat = {
  distributor: string
  count: number
}
